import { Exclude, Expose, Type } from 'class-transformer';
import { JobLotReportInterface } from '../interfaces/job-lot-report.interface';
import { ApiProperty, ApiPropertyOptional } from '@nestjs/swagger';
import {
  IsBoolean,
  IsISO8601,
  IsNumber,
  IsOptional,
  Min,
  ValidateNested,
} from 'class-validator';
import { PhaseStructureTypeEnum } from '../../phase/enums/phase-structure-type.enum';
import { GeographyPointDto } from '../../common/dto/geography-point.dto';

@Exclude()
export class JobLotReportDto implements JobLotReportInterface {
  @ApiProperty({
    type: 'string',
    format: 'uuid',
    title: 'Job Id',
  })
  @Expose()
  id!: string;

  @ApiProperty({
    type: 'string',
    format: 'uuid',
    title: 'Job Id Number',
  })
  @Expose()
  idNumber!: string | null;

  @ApiProperty({
    type: 'string',
    format: 'uuid',
    title: 'Subdivision Id',
  })
  @Expose()
  subdivisionId!: string;

  @ApiProperty({
    type: 'string',
    format: 'uuid',
    title: 'Region Id',
  })
  @Expose()
  regionId!: string;

  @ApiProperty({
    type: 'string',
    format: 'uuid',
    title: 'Builder Id',
  })
  @Expose()
  builderId!: string;

  @ApiProperty({
    type: 'string',
    title: 'Supervisor Full Names',
  })
  @Expose()
  supervisorFullNames!: string;

  @ApiProperty({
    type: 'string',
    title: 'Supervisor Ids',
  })
  @Expose()
  supervisorUserIds!: string;

  @ApiProperty({
    type: 'boolean',
    title: 'highCeiling',
  })
  @Expose()
  highCeiling!: boolean;

  @ApiProperty({
    type: 'boolean',
    title: 'garageFirst',
  })
  @Expose()
  garageFirst!: boolean;

  @ApiProperty({
    type: 'boolean',
    title: 'screwInspection',
  })
  @Expose()
  screwInspection!: boolean;

  @ApiProperty({
    type: 'string',
    title: 'Builder Name',
  })
  @Expose()
  builderName!: string;

  @ApiProperty({
    type: 'string',
    title: 'Subdivision Name',
  })
  @Expose()
  subdivisionName!: string;

  @ApiProperty({
    type: 'string',
    title: 'Region Name',
  })
  @Expose()
  regionName!: string;

  @ApiProperty({
    type: 'string',
    title: 'Job Lot',
  })
  @Expose()
  lot!: string;

  @ApiProperty({
    type: 'string',
    title: 'Job Block',
  })
  @Expose()
  block!: string | null;

  @ApiProperty({
    type: 'string',
    title: 'Created At',
  })
  @Expose()
  createdAt!: Date;

  @ApiProperty({
    type: 'string',
    format: 'uuid',
    title: 'Building Id',
  })
  @Expose()
  buildingId!: string;

  @ApiProperty({
    type: 'string',
    title: 'Building Number',
  })
  @Expose()
  buildingNumber!: string;

  @ApiProperty({
    type: 'string',
    title: 'Model name',
  })
  @Expose()
  modelName!: string;

  @ApiProperty({
    type: 'string',
    format: 'uuid',
    title: 'Model Id',
  })
  @Expose()
  modelId!: string;

  @ApiProperty({
    type: 'string',
    title: 'Address1',
  })
  @Expose()
  address1!: string;

  @ApiProperty({
    type: 'string',
    title: 'Address1',
  })
  @Expose()
  address2: string | undefined;

  @ApiProperty({
    type: 'string',
    title: 'City',
  })
  @Expose()
  city!: string;

  @ApiProperty({
    type: 'string',
    title: 'Zip',
  })
  @Expose()
  zip!: string;

  @ApiProperty({
    type: 'string',
    title: 'State',
  })
  @Expose()
  state!: string;

  @ApiProperty({
    type: 'string',
    title: 'options',
  })
  @Expose()
  options: string | undefined;

  @ApiProperty({
    type: 'string',
    title: 'houseMarked',
  })
  @Expose()
  houseMarked!: boolean;

  @ApiProperty({
    type: 'string',
    title: 'archived',
  })
  @Expose()
  archived?: boolean;

  @ApiProperty({
    type: 'string',
    title: 'currentPhaseId',
  })
  @Expose()
  currentJobPhaseId!: string;

  @ApiProperty({
    type: 'string',
    title: 'userId',
  })
  @Expose()
  userId!: string;

  @ApiProperty({
    type: 'string',
    title: 'fieldCoordinatorUserId',
  })
  @Expose()
  fieldCoordinatorUserId!: string;

  @ApiProperty({
    type: 'string',
    title: 'supervisorUserId',
  })
  @Expose()
  supervisorUserId!: string;

  @ApiProperty({
    type: 'boolean',
    default: false,
    title: 'reqBumpoutTimesheet?',
    description: 'reqBumpoutTimesheet',
  })
  @IsBoolean()
  @Type(() => Boolean)
  @Expose()
  reqBumpoutTimesheet!: boolean;

  @ApiProperty({
    type: 'boolean',
    default: false,
    title: 'interiorOptionsFinished?',
    description: 'interiorOptionsFinished',
  })
  @IsBoolean()
  @Type(() => Boolean)
  @Expose()
  interiorOptionsFinished!: boolean;

  @ApiProperty({
    type: 'boolean',
    default: false,
    title: 'exteriorOptionsFinished?',
    description: 'exteriorOptionsFinished',
  })
  @IsBoolean()
  @Type(() => Boolean)
  @Expose()
  exteriorOptionsFinished!: boolean;

  @ApiProperty({
    type: 'boolean',
    default: false,
    title: 'trimOptionsFinished?',
    description: 'trimOptionsFinished',
  })
  @IsBoolean()
  @Type(() => Boolean)
  @Expose()
  trimOptionsFinished!: boolean;

  @ApiProperty({
    title: 'Priority',
    description: 'The priority stamped from the phase at time of creation',
  })
  @IsNumber()
  @Expose()
  currentJobPhasePriority!: number;

  @ApiProperty({
    type: 'string',
    title: 'currentSupplierName',
  })
  @Expose()
  currentSupplierName!: string;

  @ApiProperty({
    type: 'string',
    format: 'date-time',
    nullable: true,
    title: 'Date Due',
    description: 'The date and time the job is due',
  })
  @IsISO8601({ strict: true })
  @Expose()
  dateDue!: Date | null;

  @ApiProperty({
    type: 'string',
    format: 'date-time',
    nullable: true,
    title: 'dateHouseMarked',
    description: 'The date and time the job dateHouseMarked',
  })
  @IsISO8601({ strict: true })
  @Expose()
  dateHouseMarked!: Date | null;

  @ApiProperty({
    type: 'boolean',
    default: false,
    title: 'currentJobPhaseApproved?',
    description: 'True when the job phase has been approved.',
  })
  @IsBoolean()
  @Type(() => Boolean)
  @Expose()
  currentJobPhaseApproved!: boolean;

  @ApiProperty({
    title: 'totalSquareFootage',
    description: 'TotalSquareFootage',
  })
  @IsNumber()
  @Expose()
  totalSquareFootage?: number | undefined;

  @ApiProperty({
    type: 'string',
    title: 'PhaseStructureType',
  })
  @Expose()
  phaseStructureType!: string;

  @ApiProperty({
    type: 'boolean',
    default: false,
    title: 'hasTrim?',
    description: 'HasTrim',
  })
  @IsBoolean()
  @Type(() => Boolean)
  @Expose()
  hasTrim!: boolean;

  @ApiProperty({
    type: 'boolean',
    default: false,
    title: 'hasInteriorOptions?',
    description: 'HasInteriorOptions',
  })
  @IsBoolean()
  @Type(() => Boolean)
  @Expose()
  hasInteriorOptions!: boolean;

  @ApiProperty({
    title: 'interiorOptionCost',
    description: 'InteriorOptionCost',
  })
  @IsNumber()
  @Expose()
  interiorOptionCost?: number | undefined;

  @ApiProperty({
    type: 'boolean',
    default: false,
    title: 'hasExteriorOptions?',
    description: 'HasExteriorOptions',
  })
  @IsBoolean()
  @Type(() => Boolean)
  @Expose()
  hasExteriorOptions!: boolean;

  @ApiProperty({
    title: 'exteriorOptionCost',
    description: 'ExteriorOptionCost',
  })
  @IsNumber()
  @Expose()
  exteriorOptionCost?: number | undefined;

  @ApiProperty({
    type: 'boolean',
    default: false,
    title: 'hasTrimOptions?',
    description: 'HasTrimOptions',
  })
  @IsBoolean()
  @Type(() => Boolean)
  @Expose()
  hasTrimOptions!: boolean;

  @ApiProperty({
    title: 'trimOptionCost',
    description: 'TrimOptionCost',
  })
  @IsNumber()
  @Expose()
  trimOptionCost?: number | undefined;

  @ApiProperty({
    title: 'Interior Options Finished Job Phase ID',
    nullable: true,
  })
  @Expose()
  interiorOptionsFinishedJobPhaseId?: string;

  @ApiProperty({
    title: 'Exterior Options Finished Job Phase ID',
    nullable: true,
  })
  @Expose()
  exteriorOptionsFinishedJobPhaseId?: string;

  @ApiProperty({
    title: 'Trim Options Finished Job Phase ID',
    nullable: true,
  })
  @Expose()
  trimOptionsFinishedJobPhaseId?: string;

  @ApiPropertyOptional({
    title: 'Interior Calculated Cost',
    description: 'Calculated cost for interior options',
    nullable: true,
  })
  @IsNumber()
  @Expose()
  interiorCalculatedCost!: number | null;

  @ApiPropertyOptional({
    title: 'Exterior Calculated Cost',
    description: 'Calculated cost for exterior options',
    nullable: true,
  })
  @IsNumber()
  @Expose()
  exteriorCalculatedCost!: number | null;

  @ApiPropertyOptional({
    title: 'Trim Calculated Cost',
    description: 'Calculated cost for trim options',
    nullable: true,
  })
  @IsNumber()
  @Expose()
  trimCalculatedCost!: number | null;

  @ApiPropertyOptional({
    type: () => GeographyPointDto,
    nullable: true,
    default: null,
    title: 'Job Geo',
    description: 'GPS location of the job.',
  })
  @IsOptional()
  @ValidateNested()
  @Type(() => GeographyPointDto)
  @Expose()
  jobGeo!: GeographyPointDto | null;

  @ApiPropertyOptional({
    title: 'Job Geo Tolerance',
    description:
      'Distance tolerance in meters used for geo matching on this job.',
    nullable: true,
  })
  @IsOptional()
  @IsNumber()
  @Min(0)
  @Expose()
  jobGeoTolerance!: number | null;
}
